/*#
# glass-gamepad (b3dGamepad)

A **split** on-screen ("glass") gamepad for touch contexts, as a Component (so
it's both the overlay element *and* a [virtual-gamepad](/virtual-gamepad/) `GamepadSource`, like
`gameController`/`keyboardGamepad`). It is laid out like a pair of VR
controllers held flat, in three clusters pinned along the bottom of the view:

| Cluster | Anchor | Controls |
| --- | --- | --- |
| `left` | bottom-left | left trigger and bumper, left stick and its click, X, Y |
| `right` | bottom-right | right trigger and bumper, right stick and its click, A, B |
| `center` | bottom-center | view, menu, and the d-pad when you ask for it |

The face buttons wear Xbox colours (A green, B red, X blue, Y yellow) in a
Quest controller's places. Under each stick is its **click** (L3, R3): on glass
a press on the stick itself cannot be told from the start of a drag, so the
click is its own button.

**The d-pad is hidden unless you name it** (`controls="…,dpad"`). View and
menu are shown by default for now; **they will become opt-in in a future
release**, so name them if your scene uses them. Named, they also appear on
the system bar in a headset, where the controllers have no view or menu button
(a thumbstick click is a stick press).

The art is one sheet (`static/glass-gamepad.svg`, built into the library), and the pad
cuts it into the clusters, each cropped to what it shows plus each stick's full
reach. Its paths are labelled by `id` (copied to `data-part` on load). When the
three do not fit side by side (a d-pad on a phone in portrait), the centre
cluster sits above the others.

Usually you don't place this yourself: set the `gamepad` attribute on
[[tosi-b3d]] and it mounts one and wires it into the active input system. Placed
directly, it's a `GamepadSource` whose `poll()` merges all clusters.

```js
import { b3d, b3dGamepad, b3dSkybox } from 'tosijs-3d'
import { demoSun, patternGround } from 'tosijs-3d/demo-utils'
import { elements } from 'tosijs'
const { div, pre } = elements

// fade OFF in the demo: this page is where you LOOK at the pad, and any
// keypress (a doc search, a scroll with the arrow keys) would fade it out
// mid-inspection. Pointer movement never fades it.
const pad = b3dGamepad({ fade: 'off' })
const readout = pre({ class: 'readout' })
let rover, mat

const scene = b3d(
  {
    sceneCreated(el, BABYLON) {
      // FIXED view (no attachControl) so the pad owns every pointer — you drag a stick, not the
      // camera. This is the rare demo where a hand-rolled camera is right: the pad is the subject.
      const cam = new BABYLON.ArcRotateCamera('cam', -Math.PI / 2, Math.PI / 3.4, 13, new BABYLON.Vector3(0, 0.6, 0), el.scene)
      el.setActiveCamera(cam)
      rover = BABYLON.MeshBuilder.CreateBox('rover', { width: 1.4, height: 0.8, depth: 1.4 }, el.scene)
      rover.position.y = 0.5
      mat = new BABYLON.StandardMaterial('rover-mat', el.scene)
      mat.diffuseColor = new BABYLON.Color3(0.35, 0.6, 0.95)
      rover.material = mat
      el.register?.({ meshes: [rover] }) // cast a shadow
      el.scene.registerBeforeRender(() => {
        const s = pad.poll()
        // left stick drives it around; right stick spins it; A hops; B/X/Y recolour it.
        rover.position.x = Math.max(-9, Math.min(9, rover.position.x + s.leftStickX * 0.12))
        rover.position.z = Math.max(-9, Math.min(9, rover.position.z + s.leftStickY * 0.12))
        rover.rotation.y += s.rightStickX * 0.06
        const lift = s.buttonA > 0.5 ? 1.7 : 0.5
        rover.position.y += (lift - rover.position.y) * 0.2
        if (s.buttonB > 0.5) mat.diffuseColor.set(0.95, 0.4, 0.35)
        else if (s.buttonX > 0.5) mat.diffuseColor.set(0.4, 0.9, 0.55)
        else if (s.buttonY > 0.5) mat.diffuseColor.set(0.95, 0.85, 0.35)
        const held = ['A', 'B', 'X', 'Y'].filter((b) => s['button' + b] > 0.5)
        readout.textContent =
          `L ${s.leftStickX.toFixed(2)},${s.leftStickY.toFixed(2)}   R ${s.rightStickX.toFixed(2)},${s.rightStickY.toFixed(2)}` +
          (held.length ? '   ' + held.join(' ') : '')
      })
    },
  },
  demoSun(),
  b3dSkybox({ timeOfDay: 11 }),
  patternGround({ size: 22 }),
  // INSIDE the scene, as `gamepad: true` mounts it, so it goes full screen
  // with it (a pad beside the scene is left behind).
  pad,
)

// The pad's clusters sit OVER the scene: touch/drag them to drive the cube.
preview.append(div({ class: 'glass-stage' }, scene), readout)
```
```css
.preview { display: flex; flex-direction: column; height: 100%; }
.glass-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  background: #0b0f14;
  border-radius: 8px;
  overflow: hidden;
}
.glass-stage tosi-b3d,
.glass-stage tosi-b3d-gamepad {
  position: absolute;
  inset: 0;
}
.readout {
  margin: 0;
  padding: 4px 8px;
  background: #222;
  border-radius: 5px;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  color: #fff;
  white-space: pre;
  pointer-events: none;
}
```

## Attributes

| Attribute | Default | Purpose |
| --- | --- | --- |
| `controls` | `''` (all but the d-pad) | **Comma**-separated piece list, e.g. `'a,b,right_stick(40,0),menu'`. Empty (the default) shows everything except the d-pad. `a/b/x/y` map to `A/B/X/Y`; `dpad` expands to the four directions; the clicks are `left_stick_click` and `right_stick_click`; `name(dx,dy)` nudges one piece. Naming `view` or `menu` also puts them on the headset's system bar |
| `scale` | `1` | Size multiplier for every cluster |
| `deadzone` | `0.15` | Stick travel (fraction of the radius) ignored at the centre |
| `maxZone` | `0.85` | Stick travel that counts as full deflection, so you need not reach the rim |
| `fade` | `'on'` | Hide the pad once a real input device is in use. `'off'` pins it visible — what you want on a page where the pad itself is the subject |
| `idleSeconds` | `10` | Seconds of silence before a faded pad comes back |
| `fadedOpacity` | `0` | How faded is faded. `0` is invisible; a low value like `0.15` leaves a hint that the fallback exists |

### What fading listens to, and what it ignores

Only a **keypress** or a **physical gamepad** fades the pad. The pointer never
does — not touch, not mouse, not trackpad.

That is a deliberate asymmetry rather than an oversight. The pad is *operable by
pointer*: `pointerdown` drives the sticks. Fading on pointer movement would make it
vanish exactly as you reached for it. A keypress is the honest signal, because it is the one
that actually implies an on-screen fallback isn't needed.

If the pad is disappearing on you during development, `fade="off"` (or
`setFade(false)`) pins it; `faded` reports the current state.

## Getting out of the way of a menu

The pad sits over the canvas, so a menu drawn in the scene would be under it
and a button beneath a stick could not be pressed. The pad therefore steps
aside by itself while the scene is paused, while a death panel is up and
while a modal popup (`openPopup({ modal: true })`) is open. That reaches a pad
inside the scene element (where `gamepad: true` puts it) or directly beside
it. For any other menu of your own, say so on the pad you mounted:

```javascript
// `pad` is your b3dGamepad(), or scene.querySelector('tosi-b3d-gamepad')
// when `gamepad: true` mounted it for you.
pad.suppress('shop') // gone, and it reports no input
pad.release('shop') // back, unless something else still holds it
```

Each caller names its own reason, so two menus cannot release each other's
hold. This is separate from the fade: `setFade` is about a real keyboard or
controller being in use. There is no switch to keep the pad up over a paused
scene; if you need one, call `release('pause')` from a `pause` listener.
*/
/*{ "parent": "input", "order": 30 }*/

import { isOff } from './b3d-utils.js'
import { Component, elements, StyleSheet } from 'tosijs'
import {
  TouchGamepadSource,
  type TouchGamepadOptions,
} from './touch-gamepad.js'
import {
  type GamepadSource,
  type VirtualGamepad,
  emptyGamepad,
  mergeGamepads,
} from './virtual-gamepad.js'
import { glassGamepadSvg } from './gamepad-art.js'
import { realNow } from './clock.js'

const { div } = elements

export type ClusterAnchor =
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center'
  | 'top-left'
  | 'top-right'
  | 'top-center'

// Offsets in cqmin so they scale with the host container (see `container-type`).
const ANCHOR_CSS: Record<ClusterAnchor, string> = {
  'bottom-left': 'left:2cqmin;bottom:2cqmin',
  'bottom-right': 'right:2cqmin;bottom:2cqmin',
  'bottom-center': 'left:50%;bottom:2cqmin;transform:translateX(-50%)',
  'top-left': 'left:2cqmin;top:2cqmin',
  'top-right': 'right:2cqmin;top:2cqmin',
  'top-center': 'left:50%;top:2cqmin;transform:translateX(-50%)',
}

// Light-DOM component, so the host page (or b3d) can style the clusters and the
// `.active` press highlight reaches them. Inject the layout + default highlight
// once, scoped to the tag. `container-type: size` makes the cluster cqmin units
// scale to the host's own size (the demo card / the b3d view), not the viewport.
function ensureGamepadStyles(): void {
  if (typeof document === 'undefined') return
  // StyleSheet dedups by id and is idempotent, so no manual guard is needed.
  StyleSheet('tosi-b3d-gamepad-styles', {
    'tosi-b3d-gamepad': {
      position: 'absolute',
      inset: 0,
      display: 'block',
      pointerEvents: 'none',
      zIndex: 15,
      containerType: 'size',
    },
    'tosi-b3d-gamepad .pad-clusters': {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
    },
    'tosi-b3d-gamepad [data-part]': {
      opacity: 0.25,
      strokeWidth: 8,
      transition: 'stroke-width .08s, filter .08s',
    },
    'tosi-b3d-gamepad [data-part].active': {
      opacity: 0.75,
      strokeWidth: 16,
      filter: 'brightness(1.35)',
    },
  })
}

// The static cluster SVGs label paths by `id`; copy these (only) to `data-part`
// so TouchGamepadSource finds them — and so multiple instances don't clash on a
// global id.
const PART_IDS = new Set([
  'A',
  'B',
  'X',
  'Y',
  'left_bumper',
  'right_bumper',
  'left_trigger',
  'right_trigger',
  'dpad_up',
  'dpad_down',
  'dpad_left',
  'dpad_right',
  'menu',
  'view',
  'left_stick',
  'left_stick_travel',
  'left_stick_click',
  'right_stick',
  'right_stick_travel',
  'right_stick_click',
])

export type ClusterConfig = {
  /** SVG to load for this cluster. The default is the built-in sheet, cut to
   * this cluster's parts; any other url is shown whole. */
  url?: string
  /** Which corner to pin it to. */
  anchor?: ClusterAnchor
  /** Width of the cluster overlay in cqmin — relative to the host's own size,
   * so it scales with the view/card (multiplied by `scale`). The default sizes
   * every cluster cut from the sheet at one scale. */
  vmin?: number
}

export type ClusterKey = 'left' | 'right' | 'center'

/** The built-in art: ONE sheet, cut into the three clusters. */
const SHEET_URL = '/glass-gamepad.svg'
/** cqmin per sheet unit: a side cluster (~320 units) is ~42cqmin wide. */
const SHEET_SCALE = 42 / 322

const DEFAULTS: Record<ClusterKey, { url: string; anchor: ClusterAnchor }> = {
  left: { url: SHEET_URL, anchor: 'bottom-left' },
  right: { url: SHEET_URL, anchor: 'bottom-right' },
  center: { url: SHEET_URL, anchor: 'bottom-center' },
}

// Which controls each cluster owns — used to cut the sheet, to skip empty
// clusters and to hide controls that weren't requested. Sticks list both knob
// and travel. Laid out like a pair of VR controllers: X/Y with the left stick,
// A/B with the right.
const CLUSTER_PARTS: Record<ClusterKey, string[]> = {
  left: [
    'left_stick',
    'left_stick_travel',
    'left_stick_click',
    'left_bumper',
    'left_trigger',
    'X',
    'Y',
  ],
  right: [
    'A',
    'B',
    'right_stick',
    'right_stick_travel',
    'right_stick_click',
    'right_bumper',
    'right_trigger',
  ],
  center: ['dpad_up', 'dpad_down', 'dpad_left', 'dpad_right', 'view', 'menu'],
}

const DPAD = ['dpad_up', 'dpad_down', 'dpad_left', 'dpad_right']

/** Every control the pad shows when none are named: all but the d-pad. */
export const DEFAULT_CONTROLS: string[] = Object.values(CLUSTER_PARTS)
  .flat()
  .filter((p) => !DPAD.includes(p))

export type Box = { x: number; y: number; width: number; height: number }

/**
 * The viewBox that shows a cluster cut from the sheet: its parts' boxes, grown
 * by `pad` (half the widest stroke), and every stick's full REACH — the travel
 * circle's radius plus the knob's, around the travel's centre — so a thumb
 * dragged to the rim is never clipped by the cluster's edge.
 */
export function cropBox(
  parts: Box[],
  pad: number,
  sticks: { travel: Box; knob: Box }[] = []
): Box {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  const grow = (x: number, y: number, x2: number, y2: number) => {
    x0 = Math.min(x0, x)
    y0 = Math.min(y0, y)
    x1 = Math.max(x1, x2)
    y1 = Math.max(y1, y2)
  }
  for (const b of parts)
    grow(b.x - pad, b.y - pad, b.x + b.width + pad, b.y + b.height + pad)
  for (const { travel, knob } of sticks) {
    const cx = travel.x + travel.width / 2
    const cy = travel.y + travel.height / 2
    const reach = travel.width / 2 + knob.width / 2
    grow(cx - reach, cy - reach, cx + reach, cy + reach)
  }
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 }
}

/**
 * The box around a path's points, read from its `d` (absolute coordinates, as
 * the art is exported). A curve lies inside its control points, so this can
 * only be a little generous, never short.
 */
export function pathBox(d: string): Box | null {
  const n = (d.match(/-?\d*\.?\d+(?:e-?\d+)?/gi) ?? []).map(Number)
  if (n.length < 2) return null
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (let i = 0; i + 1 < n.length; i += 2) {
    x0 = Math.min(x0, n[i])
    x1 = Math.max(x1, n[i])
    y0 = Math.min(y0, n[i + 1])
    y1 = Math.max(y1, n[i + 1])
  }
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 }
}

const NAME_ALIASES: Record<string, string[]> = {
  a: ['A'],
  b: ['B'],
  x: ['X'],
  y: ['Y'],
  dpad: ['dpad_up', 'dpad_down', 'dpad_left', 'dpad_right'],
}

/**
 * Parse a gamepad spec — e.g. `"a,b,right_stick(40,0),menu"` — into the controls
 * to show and any per-piece offsets. `''` / `'true'` → all controls. `a/b/x/y`
 * map to `A/B/X/Y`; `dpad` expands to the four directions.
 */
export function parseGamepadControls(spec: string): {
  controls?: string[]
  offsets: Record<string, { x: number; y: number }>
} {
  const offsets: Record<string, { x: number; y: number }> = {}
  const s = spec.trim()
  if (s === '' || s === 'true') return { offsets } // all controls
  const controls: string[] = []
  /*
  Tokenise, do NOT `split(',')`.

  The offset syntax contains a comma — `right_stick(40,0)` — so splitting on
  commas tore it into `right_stick(40` and `0)`, neither of which matches the
  token pattern. Both were silently dropped: the piece vanished from the pad AND
  its offset was lost, with no error. The example in this function's own JSDoc
  could not parse.

  Matching whole tokens instead means the comma inside the parens belongs to the
  token, which is what anyone writing it expects.
  */
  for (const token of s.match(/[A-Za-z_]+(?:\([^)]*\))?/g) ?? []) {
    const m = token
      .trim()
      .match(
        /^([A-Za-z_]+)(?:\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\))?$/
      )
    if (m == null) continue
    const names = NAME_ALIASES[m[1].toLowerCase()] ?? [m[1].toLowerCase()]
    for (const n of names) {
      controls.push(n)
      if (m[2] != null) offsets[n] = { x: Number(m[2]), y: Number(m[3]) }
    }
  }
  return { controls, offsets }
}

/*
THE DEFAULT ART SHIPS IN THE LIBRARY. It was fetched from `/gamepad-*.svg`,
which our doc site serves from `static/` and an adopter's site does not (it
is not in the package), so there the pad 404'd and never drew. A default url
now reads the embedded copy (`bun run dial-art` regenerates it from the SVGs);
any other url is fetched as before.
*/
const BUILT_IN: Record<string, string> = {
  [SHEET_URL]: glassGamepadSvg,
}

async function loadCluster(url: string): Promise<SVGSVGElement> {
  const text = BUILT_IN[url] ?? (await (await fetch(url)).text())
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml')
  const svg = doc.documentElement as unknown as SVGSVGElement
  for (const el of Array.from(svg.querySelectorAll('*'))) {
    // id → data-part (known parts only): so the source binds, and so global ids
    // don't collide if two gamepads are mounted.
    const id = el.getAttribute('id')
    if (id != null && PART_IDS.has(id)) {
      el.setAttribute('data-part', id)
      el.removeAttribute('id')
    }
    // Inline style → presentation attributes. The exported art bakes
    // fill/stroke/stroke-width into `style`, and inline style outranks every
    // stylesheet — so the `.active` highlight (and any themed/dynamic stroke)
    // could never override it. As attributes they're overridable, matching how
    // gamepad-svg.ts builds its themeable paths.
    const style = el.getAttribute('style')
    if (style != null) {
      for (const decl of style.split(';')) {
        const i = decl.indexOf(':')
        if (i < 0) continue
        const prop = decl.slice(0, i).trim()
        const val = decl.slice(i + 1).trim()
        if (prop !== '' && val !== '' && !el.hasAttribute(prop)) {
          el.setAttribute(prop, val)
        }
      }
      el.removeAttribute('style')
    }
  }
  return svg
}

/**
 * The split touch gamepad as a Component: the element is a full-bleed overlay
 * (pointer-events pass through except on the clusters), and the element *is* the
 * `GamepadSource` — `poll()` merges every loaded cluster. b3dInputFocus finds it
 * and adds it to the active input provider.
 */
export class B3dGamepad extends Component implements GamepadSource {
  static preferredTagName = 'tosi-b3d-gamepad'

  readonly kind = 'glass'

  static initAttributes = {
    /** Spec string: `''`/`true` = full layout, else e.g. `"a,b,left_stick"`. */
    controls: '',
    /** Scale all clusters while keeping them anchored. */
    scale: 1,
    deadzone: 0.15,
    maxZone: 0.85,
    /** Seconds of no keyboard/gamepad input before the pad fades back in. */
    idleSeconds: 10,
    /** `'off'` keeps the pad visible whatever else you're holding — for
     * screenshots, desktop demos, or a scene where it IS the control. */
    fade: 'on' as 'on' | 'off',
    /** Opacity while a real input device is in use (0 = invisible). */
    fadedOpacity: 0,
  }

  // No styleSpec → light DOM, so page/b3d CSS reaches the clusters and the
  // `.active` press highlight applies. Styling is injected globally (scoped to
  // the tag) by ensureGamepadStyles().
  content = [div({ class: 'pad-clusters', part: 'clusters' })]

  declare controls: string
  declare scale: number
  declare deadzone: number
  declare maxZone: number
  declare idleSeconds: number
  declare fade: 'on' | 'off'
  declare fadedOpacity: number

  /** Advanced: per-cluster url/anchor/vmin overrides, or `false` to omit one.
   * `top` is the old name of `center`, which sits at the bottom now. */
  clusters?: {
    left?: ClusterConfig | false
    right?: ClusterConfig | false
    center?: ClusterConfig | false
    /** @deprecated use `center` */
    top?: ClusterConfig | false
  }
  handleButton?: (part: string, pressed: boolean) => void

  private sources: TouchGamepadSource[] = []
  private built = false

  connectedCallback(): void {
    super.connectedCallback()
    ensureGamepadStyles()
    if (!this.built) {
      this.built = true
      void this._build()
    }
    this._watchRealInput()
  }

  /**
   * FADE OUT when a real input device shows up, back IN after `idleSeconds`
   * of silence. On-screen controls are a fallback for a device with no
   * keyboard or gamepad; on a laptop they sit on top of the view being useless
   * — but removing them outright breaks the tablet case, and a manual toggle
   * is a setting nobody finds.
   *
   * WHAT COUNTS AS "a real input device": a **keypress**, or a **physical
   * gamepad**. Deliberately NOT the pointer, in any of its flavours:
   *
   * - **Touch** — touching the glass pad IS using it, so it must not fade away
   *   under your thumb.
   * - **Mouse / trackpad** — a pointer proves nothing about whether there's a
   *   keyboard, and the pad is fully operable by pointer (`pointerdown` drives
   *   the sticks). Fading on pointer movement meant the pad vanished exactly as
   *   you reached for it — you moved the mouse to click a stick, and the thing
   *   you were aiming at disappeared. It made the fade read as a bug rather
   *   than as a feature, and the demo on this page had to opt out of it
   *   entirely to be usable.
   *
   * A keypress is the honest signal, because it is the one that actually
   * implies the fallback isn't needed.
   */
  private _watchRealInput(): void {
    if (this._inputWatch != null) return
    // NOTE: the watcher installs unconditionally and `fade` is read INSIDE
    // `wake`. It used to be read here, once, which made the attribute inert at
    // runtime in both directions — including from a `scenePanel` toggle, this
    // repo's own recommended way to expose a tweakable. A settings control that
    // silently does nothing is worse than no control.
    const wake = () => {
      if (isOff(this.fade)) return
      if (this._idleTimer != null) clearTimeout(this._idleTimer)
      this._setFaded(true)
      this._idleTimer = setTimeout(
        () => this._setFaded(false),
        Math.max(1, this.idleSeconds) * 1000
      ) as unknown as number
    }
    const onPad = () => {
      const pads = navigator.getGamepads?.() ?? []
      for (const p of pads) {
        if (p == null) continue
        if (
          p.buttons.some((b) => b.pressed) ||
          p.axes.some((a) => Math.abs(a) > 0.4)
        ) {
          wake()
          return
        }
      }
    }
    window.addEventListener('keydown', wake)
    // The Gamepad API has no "input happened" event, so a physical stick has
    // to be polled — cheaply, and only while we're visible.
    const padPoll = setInterval(onPad, 500) as unknown as number
    this._inputWatch = () => {
      window.removeEventListener('keydown', wake)
      clearInterval(padPoll)
      if (this._idleTimer != null) clearTimeout(this._idleTimer)
    }
  }

  /**
   * Is the pad currently hidden by the fade behaviour?
   *
   * Public because the fade is production-correct but development-hostile: once
   * a keyboard is in use the pad goes away and (short of an input
   * drought) doesn't come back, so checking it on a laptop meant reaching for
   * Chrome's responsive mode. `<tosi-b3d>` puts a gamepad gadget in the gear
   * panel that reads and flips this.
   */
  get hidden(): boolean {
    return this._faded
  }

  /**
   * Force the pad visible (`false`) or hand it back to the fade behaviour
   * (`true`). Reconciles immediately — a toggle that waited for the next
   * pointer move would read as broken.
   */
  setFade(on: boolean): void {
    this.fade = on ? 'on' : 'off'
    if (this._idleTimer != null) {
      clearTimeout(this._idleTimer)
      this._idleTimer = null
    }
    if (!on) this._setFaded(false)
  }

  private _setFaded(faded: boolean): void {
    if (faded === this._faded) return
    this._faded = faded
    this.style.transition = 'opacity 0.4s'
    this.style.opacity = faded ? String(this.fadedOpacity) : '1'
    // Faded controls must not eat clicks meant for the scene behind them.
    this.style.pointerEvents = faded ? 'none' : ''
  }

  private _suppressedBy = new Set<string>()

  /** Is the pad stepping aside for a menu (see `suppress`)? */
  get suppressed(): boolean {
    return this._suppressedBy.size > 0
  }

  /**
   * Hide the pad and stop it reporting input until `release` is called with
   * the same reason. For a modal drawn in the scene, which would otherwise sit
   * under the pad. Reasons are counted separately.
   */
  suppress(reason = 'modal'): void {
    this._suppressedBy.add(reason)
    // `display`, not `visibility`: parts of the pad set their own visibility,
    // so a hidden pad still drew bits of itself and took presses meant for
    // the panel under it.
    this.style.display = 'none'
  }

  release(reason = 'modal'): void {
    this._suppressedBy.delete(reason)
    if (this._suppressedBy.size === 0) this.style.display = ''
  }

  private _inputWatch: (() => void) | null = null
  private _idleTimer: number | null = null
  private _faded = false

  private async _build(): Promise<void> {
    const host = this.parts.clusters as HTMLElement
    const { controls } = parseGamepadControls(String(this.controls ?? ''))
    const scale = this.scale ?? 1
    const opts: TouchGamepadOptions = {
      deadzone: this.deadzone,
      maxZone: this.maxZone,
      handleButton: this.handleButton,
    }
    // The set of controls to show: the default leaves the d-pad out. A stick
    // implies its travel.
    const want = new Set(controls ?? DEFAULT_CONTROLS)
    if (want.has('left_stick')) want.add('left_stick_travel')
    if (want.has('right_stick')) want.add('right_stick_travel')
    for (const key of ['left', 'right', 'center'] as const) {
      const c =
        key === 'center'
          ? this.clusters?.center ?? this.clusters?.top
          : this.clusters?.[key]
      if (c === false) continue
      if (!CLUSTER_PARTS[key].some((p) => want.has(p))) continue
      const cfg: ClusterConfig = { ...DEFAULTS[key], ...c }
      const url = cfg.url ?? SHEET_URL
      let svg: SVGSVGElement
      try {
        svg = await loadCluster(url)
      } catch (err) {
        console.warn('b3dGamepad: failed to load', url, err)
        continue
      }
      if (!this.isConnected) return
      const cut = url === SHEET_URL
      for (const el of Array.from(svg.querySelectorAll('[data-part]'))) {
        const p = el.getAttribute('data-part')!
        // The sheet holds every cluster: drop the parts that are not this
        // one's, so they take no presses. Unwanted parts are hidden.
        if (cut && !CLUSTER_PARTS[key].includes(p)) el.remove()
        else if (!want.has(p)) el.setAttribute('display', 'none')
      }
      svg.setAttribute(
        'style',
        `position:absolute;${ANCHOR_CSS[cfg.anchor ?? DEFAULTS[key].anchor]};` +
          `height:auto;pointer-events:auto;visibility:hidden;` +
          `touch-action:none;user-select:none;-webkit-user-select:none`
      )
      host.appendChild(svg)
      let width = cfg.vmin ?? 42
      if (cut) {
        // Read from the path data, not measured: getBBox needs the pad laid
        // out, and one built while hidden or unsized (a maximised example)
        // measured zero and drew nothing.
        const shown = (p: string) => {
          const el = svg.querySelector(`[data-part="${p}"]`)
          return el != null && el.getAttribute('display') !== 'none'
            ? pathBox(el.getAttribute('d') ?? '')
            : null
        }
        const parts = CLUSTER_PARTS[key]
          .map(shown)
          .filter((b): b is Box => b != null)
        const sticks = (['left_stick', 'right_stick'] as const)
          .map((s) => [shown(`${s}_travel`), shown(s)] as const)
          .filter(([t, k]) => t != null && k != null)
          .map(([t, k]) => ({ travel: t!, knob: k! }))
        const box = cropBox(parts, 8, sticks)
        svg.setAttribute(
          'viewBox',
          `${box.x} ${box.y} ${box.width} ${box.height}`
        )
        svg.removeAttribute('width')
        svg.removeAttribute('height')
        width = cfg.vmin ?? box.width * SHEET_SCALE
      }
      svg.style.width = `${width * scale}cqmin`
      svg.style.visibility = ''
      const vb = svg.viewBox.baseVal
      this._placed[key] = {
        svg,
        width: width * scale,
        height:
          vb != null && vb.width > 0
            ? (width * scale * vb.height) / vb.width
            : 0,
        anchored: c?.anchor != null,
      }
      this.sources.push(new TouchGamepadSource(svg, opts))
    }
    this._layout()
    this._resize ??= new ResizeObserver(() => this._layout())
    this._resize.observe(this)
  }

  private _placed: Partial<
    Record<
      ClusterKey,
      { svg: SVGSVGElement; width: number; height: number; anchored: boolean }
    >
  > = {}
  private _resize?: ResizeObserver

  /*
  THE CENTRE CLUSTER TUCKS IN AT THE BOTTOM WHEN IT FITS, and is lifted above
  the side clusters when it does not. Clusters are sized in cqmin, so on a
  phone in portrait (where the width is the short side) they are wider
  together than the screen. Whether they clash is decided by the drawn parts,
  not the clusters' boxes: view and menu alone fit between the corner
  clusters' sticks, and a d-pad does not.
  */
  private _layout(): void {
    const { left, right, center } = this._placed
    if (center == null || center.anchored) return
    if (this.clientWidth === 0 || this.clientHeight === 0) return
    const margin = 2
    center.svg.style.bottom = `${margin}cqmin`
    const rects = (svg?: SVGSVGElement) =>
      svg == null
        ? []
        : Array.from(svg.querySelectorAll('[data-part]'))
            .filter((el) => el.getAttribute('display') !== 'none')
            .map((el) => el.getBoundingClientRect())
    // Corners may touch by a few px (view meets X's corner on an iPhone in
    // portrait); a real overlap lifts the cluster.
    const gap = -4
    const mine = rects(center.svg)
    const theirs = [...rects(left?.svg), ...rects(right?.svg)]
    const clash = mine.some((a) =>
      theirs.some(
        (b) =>
          a.left < b.right + gap &&
          b.left < a.right + gap &&
          a.top < b.bottom + gap &&
          b.top < a.bottom + gap
      )
    )
    if (clash) {
      const above = Math.max(left?.height ?? 0, right?.height ?? 0)
      center.svg.style.bottom = `${2 * margin + above}cqmin`
    }
  }

  /** Did the `controls` spec name this part outright (not the default set)? */
  requested(part: string): boolean {
    return (
      parseGamepadControls(String(this.controls ?? '')).controls?.includes(
        part
      ) ?? false
    )
  }

  /**
   * Press a button from somewhere else for a moment: the headset's system bar
   * presses view and menu this way, since the pad itself is not there. Held
   * for `ms`, long enough for every controllable's edge detector to see it.
   */
  pulse(part: 'view' | 'menu', ms = 150): void {
    this._pulses[part] = realNow() + ms
  }
  private _pulses: Partial<Record<'view' | 'menu', number>> = {}

  poll(): VirtualGamepad {
    let merged = emptyGamepad()
    // A pulse is a deliberate press, so it gets through a suppressed pad.
    const now = realNow()
    for (const [part, until] of Object.entries(this._pulses))
      if (until > now) merged[part as 'view' | 'menu'] = 1
    // A stick held when a menu came up must not keep steering behind it.
    if (this._suppressedBy.size > 0) return merged
    for (const s of this.sources) merged = mergeGamepads(merged, s.poll())
    return merged
  }

  /** Mirror external gamepad state (hardware/keyboard) onto untouched controls. */
  reflectState(pad: VirtualGamepad): void {
    for (const s of this.sources) s.reflectState(pad)
  }

  disconnectedCallback(): void {
    this._resize?.disconnect()
    this._resize = undefined
    this._inputWatch?.()
    this._inputWatch = null
    for (const s of this.sources) s.dispose()
    this.sources = []
    super.disconnectedCallback()
  }
}

export const b3dGamepad = B3dGamepad.elementCreator()
